import { Suspense, useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import LoggedOut from "../container/LoggedOut";
import AdminRoutes from "../container/PrivateRoutes/admin";
import Unauthorized from "../container/Unauthorized";
import LoginPage from "../container/LoginPage";
import { dashboardActions } from "../redux/slices/dashboard/dashboardSlice";
import { featureActions } from "../redux/slices/feature/featureSlice";
import chatRoutes from "./routes/chatRoutes";
import { digitalDashboard } from "./routes/digitalDashboard";
import { newAdminPanelRoutes } from "./routes/newAdminPanelRoutes";
import { oneAppRoutes } from "./routes/oneAppRoutes.js";
import Loader from "../components/Loader";
import ErrorBoundary from "../components/ErrorBoundary";
import apiService from "../services/apiService";
import { trackEvent } from "../services/gaTracking";
import { GA_EVENTS } from "../constants/gaEvents";
import {
  CREDENTIAL_SESSION,
  INTERNAL_LOGIN_PARAM,
  TOKEN_NAME,
  USER_SESSION_LANDED,
} from "../helper/constants";
import {
  clearSamlLoginInProgress,
  getCookie,
  redirectToSamlLogin,
} from "../helper/utils";
 
export default function Routers() {
  const dispatch = useDispatch();
  const location = useLocation();

  const [featureCheckComplete, setFeatureCheckComplete] = useState(false);
  const [showLoginPage, setShowLoginPage] = useState(false);
  // Credential login in place of the SAML hand-off (?internallogin=true).
  const [showInternalLogin, setShowInternalLogin] = useState(false);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const hasOneAppLoginParam = searchParams.get("oneapplogin") === "true";
  const hasInternalLoginParam =
    searchParams.get(INTERNAL_LOGIN_PARAM) === "true";

  // Public routes where the SSO/feature/dashboard auto-effects must NOT run.
  // Otherwise users land here (after logout / on auth failure) and the SSO
  // gate yanks them back to SAML, creating a redirect loop.
  const PUBLIC_ROUTES = ["/logout-success", "/unauthorized-user"];
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
 
  // -----------------------------
  // 1. FEATURE FLAG CHECK
  // -----------------------------
  useEffect(() => {
    let cancelled = false;

    const checkFeatureFlag = async () => {
      // Don't trigger any auth-related work on public routes
      // (e.g. /logout-success, /unauthorized-user).
      if (isPublicRoute) {
        return;
      }
      try {
        if (hasOneAppLoginParam) {
          const response = await apiService.checkFeatureStatus("IOS_LOGIN_FEATURE");

          const responseData =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;

          const isEnabled = responseData?.data?.isEnabled === true;

          if (!cancelled) {
            if (isEnabled) {
              setShowLoginPage(true);
              setFeatureCheckComplete(true);
            } else {
              const url = new URL(window.location.href);
              url.searchParams.delete("oneapplogin");
              window.location.href = url.toString();
            }
          }
        } else {
          if (!cancelled) setFeatureCheckComplete(true);
        }
      } catch {
        const url = new URL(window.location.href);
        url.searchParams.delete("oneapplogin");
        window.location.href = url.toString();
      }
    };

    checkFeatureFlag();

    return () => {
      cancelled = true;
    };
  }, [hasOneAppLoginParam, location.search, location.pathname, isPublicRoute]);
 
  // -----------------------------
  // 2. SSO GATE — web users without a session go to SAML; oneapplogin does not
  // -----------------------------
  const [siteDetailsLoaded, setSiteDetailsLoaded] = useState(false);

  const { data = {}, isLoading = true } = useSelector(
    (state) => state.dashboardFlow.homeDashboardListing || {}
  );
  const jobRole = data?.jobRole || "";
  const accessSettled = useSelector(
    (state) => state.featureFlow?.allowedIds?.settled
  );
  // fetch-access is only dispatched when the user has an employeeId/email.
  const hasAccessIdentity = !!(data?.employeeId || data?.email);
  const loginTrackedRef = useRef(false);
  // Guard so the SSO gate fires AT MOST once per page load. Without this,
  // the effect re-ran on every location change (route Navigate, query update),
  // re-dispatching /user. The second /user call could 401 and — combined with
  // user_session_landed already being set — would trip the request.js 401
  // handler which clearAuthState()s, wiping oneapp_token, then forces /logout.
  const ssoGateFiredRef = useRef(false);

  useEffect(() => {
    // Never run the SSO gate on public routes — this is what was causing
    // /logout-success to immediately bounce back to SAML.
    if (isPublicRoute) {
      return;
    }
    if (ssoGateFiredRef.current) {
      return;
    }
    if (!hasOneAppLoginParam && featureCheckComplete) {
      const hasSession = sessionStorage.getItem(USER_SESSION_LANDED) === "true";
      const returnedFromSSO = sessionStorage.getItem("sso_initiated") === "true";
      const loginPending = sessionStorage.getItem("login_pending") === "true";
      const hasLoginToken = !!getCookie(TOKEN_NAME);
      // A credential login (either non-SSO path) counts as "authenticated,
      // now go validate", exactly like a return from SAML does.
      const hasCredentialSession =
        sessionStorage.getItem(CREDENTIAL_SESSION) === "true";

      if (
        hasSession ||
        returnedFromSSO ||
        loginPending ||
        hasLoginToken ||
        hasCredentialSession
      ) {
        console.info(
          "[auth] SSO gate → dispatch /user. hasSession=",
          hasSession,
          "returnedFromSSO=",
          returnedFromSSO,
          "loginPending=",
          loginPending,
          "hasLoginToken=",
          hasLoginToken,
          "hasCredentialSession=",
          hasCredentialSession
        );
        ssoGateFiredRef.current = true;
        clearSamlLoginInProgress();
        sessionStorage.removeItem("sso_initiated");
        dispatch(dashboardActions.homeDashboardListing());
      } else if (hasInternalLoginParam) {
        // ?internallogin=true: render the credential form instead of handing
        // off to OAM. Without the param this branch is skipped and SSO stays
        // the only way in.
        console.info("[auth] SSO gate → internal credential login (param)");
        ssoGateFiredRef.current = true;
        setShowInternalLogin(true);
      } else {
        ssoGateFiredRef.current = true;
        redirectToSamlLogin("router-sso-gate");
      }
    }
  }, [
    dispatch,
    hasOneAppLoginParam,
    hasInternalLoginParam,
    featureCheckComplete,
    isPublicRoute,
  ]);

  // -----------------------------
  // 3. SITE DETAILS API — only after user API confirms valid session
  // -----------------------------
  const siteDetailsFetchedRef = useRef(false);
  useEffect(() => {
    if (isPublicRoute) {
      return;
    }
    if (siteDetailsFetchedRef.current) {
      return;
    }
    if (!hasOneAppLoginParam && !isLoading && data && Object.keys(data).length > 0) {
      siteDetailsFetchedRef.current = true;
      // Call user API first, then use response to fetch access permissions
     if (data?.employeeId || data?.email) {
        dispatch(featureActions.fetchAllowedIds({
          empId: data.employeeId,
          email: data.email,
        }));
      }

      // Call site details API along with user API
      apiService.getSiteDetails()
        .then((siteDetailsResponse) => {
          const siteDetailsData = typeof siteDetailsResponse.data === 'string'
            ? JSON.parse(siteDetailsResponse.data)
            : siteDetailsResponse.data;

          if (siteDetailsData) {
            localStorage.setItem("site_details", JSON.stringify(siteDetailsData));
            setSiteDetailsLoaded(true);
          }
        })
        .catch((siteError) => {
          console.error("Error fetching site details:", siteError);
          setSiteDetailsLoaded(true);
        });
    }
  }, [data, isLoading, hasOneAppLoginParam, isPublicRoute]);

  // Sync profileData to sessionStorage whenever it's available
  useEffect(() => {
    if (isPublicRoute) {
      return;
    }
    // Wait for both user data and site details to be loaded before tracking login
    if (!hasOneAppLoginParam && !isLoading && data && Object.keys(data).length > 0 && siteDetailsLoaded) {
      const existingUserDetails = sessionStorage.getItem("user_details");
      const loginPending = sessionStorage.getItem("login_pending");
      const loginAlreadyTracked = sessionStorage.getItem("login_event_tracked");
      let userDetails = null;
      
      if (existingUserDetails) {
        try {
          userDetails = JSON.parse(existingUserDetails);
        } catch (e) {
          userDetails = {};
        }
      }
      
      // Update or create user_details with latest data from API (especially employeeId)
      const updatedUserDetails = {
        ...userDetails,
        id: data.id || data.userId || userDetails?.id || null,
        _id: data._id || data.id || userDetails?._id || null,
        userId: data.userId || data.id || userDetails?.userId || null,
        employeeId: data.employeeId || userDetails?.employeeId || "",
        name: data.name || data.employeeName || userDetails?.name || "",
        businessModule: data.businessModule || userDetails?.businessModule || "",
        jobRole: data.jobRole || userDetails?.jobRole || "",
        location: data.location || userDetails?.location || "",
        lastLogin: data.lastLogin || userDetails?.lastLogin || new Date().toISOString(),
        ...data, // Include all other fields from API
      };
      
      sessionStorage.setItem("user_details", JSON.stringify(updatedUserDetails));
      sessionStorage.setItem("user_session_landed", "true");
      // Track login event - fires when:
      // 1. User just logged in (login_pending flag exists), OR
      // 2. First time loading after SAML login (no login_event_tracked flag)
      // Now site details are guaranteed to be in localStorage, so locationCode will be available
      const shouldTrackLogin = (loginPending === "true" || !loginAlreadyTracked) && !loginTrackedRef.current;
      
      if (shouldTrackLogin) {
        console.log("🔔 Tracking Login Event", {
          loginPending,
          loginAlreadyTracked,
          userName: updatedUserDetails.name || updatedUserDetails.employeeId
        });
        
        trackEvent(GA_EVENTS.LOGIN, {
          lastLoggedIn: updatedUserDetails.lastLogin || new Date().toISOString(),
        });
        
        loginTrackedRef.current = true;
        sessionStorage.setItem("login_event_tracked", "true");
        sessionStorage.removeItem("login_pending"); // Clear the pending flag
      }
    }
  }, [data, isLoading, hasOneAppLoginParam, siteDetailsLoaded, isPublicRoute]);
 
  // -----------------------------
  // 4. RETURN PRIORITY FLOW
  // -----------------------------

  // Loader during feature check (iOS login feature flag API)
  if (hasOneAppLoginParam && !featureCheckComplete) {
    return <Loader />;
  }

  // oneapplogin=true & feature enabled → show login page
  if (showLoginPage && featureCheckComplete) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage mode="ios" />} />
      </Routes>
    );
  }

  // internallogin=true → show the credential form for any route until a
  // session exists
  if (showInternalLogin) {
    return (
      <Routes>
        <Route path="*" element={<LoginPage mode="internal" />} />
      </Routes>
    );
  }

  // Root redirect logic
  if (location.pathname === "/") {
    if (isLoading) return <div>Loading...</div>;
    return (
      <Navigate
        to={jobRole === "kn-reports" ? "/oneapp/kn-reports" : "/oneapp/home"}
        replace
      />
    );
  }
 
  // Hold pages until /user and then fetch-access have settled. Pages fire their
  // APIs on mount, and the HO business/category filter is only persisted once
  // fetch-access resolves — mounting earlier sent those first calls without it.
  // Only the first load is held: once access has settled, the route wrappers'
  // own /user refetches (which flip isLoading back on) must not unmount pages.
  if (!isPublicRoute && !accessSettled && (isLoading || hasAccessIdentity)) {
    return <Loader />;
  }

  // Normal route render
  return (
    <ErrorBoundary label="route" resetKeys={[location.pathname]}>
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route element={<AdminRoutes />}>
            {[
              ...oneAppRoutes,
              ...newAdminPanelRoutes,
              ...digitalDashboard,
              ...chatRoutes,
            ].map((route, index) => (
              <Route
                exact
                key={index}
                path={route.Path}
                element={route.Component ? <route.Component /> : route.NavigateTo}
              />
            ))}
          </Route>
 
          <Route path="/logout-success" element={<LoggedOut />} />
          <Route path="/unauthorized-user" element={<Unauthorized />} />
          <Route path="*" element={<p>Page not found</p>} replace />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}